import { screenSizes } from 'constants/global'
import { FormattedMessage, useIntl } from 'locales/index'
import isNil from 'lodash/isNil'
import React, { useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import CommutifiTermsCheckbox from 'shared/components/common/CommutifiTermsCheckbox'
import EmailInput, { EmailField as Email } from 'shared/components/common/forms/Email'
import PasswordInput, { PasswordField as Password } from 'shared/components/common/forms/Password'
import { Button as AppButton, Form, FormInstance, FormProps, Input, Spin, FormRule } from '@commutifi-fe/ui'
import { RegisterFormPayload } from 'shared/interfaces/Account'
import { INTL_ID_VALIDATION_REQUIREMENTS } from '@commutifi-fe/utils'
import styles from './styles.module.scss'

export interface IRegisterFormProps extends FormProps {
  // Form submit callback function
  onSubmit?: (values: RegisterFormPayload) => void
  // Display a spinner over the form when loading is true
  loading?: boolean
  // Initial name value
  initialName?: string
  // If the agreement terms should be initially checked or not
  initialAgreement?: boolean
  // Initial email value
  initialEmail?: string
  // Overwrite the register button text
  registerButtonText?: React.ReactNode
  // Control disable state of register button
  registerButtonDisabled?: boolean
  children?: React.ReactNode
  /**
   * Overwrite the Register button to use custom action buttons
   * IMPORTANT: Make sure you have one button of html type 'submit'
   *            to be able to trigger submission form
   *
   * Pass 'null' to simply hide the default Register button
   */
  actions?: React.ReactNode
}

interface RegisterFormContextProps {
  registerField: (name: string) => void
}

export enum ComposedRegisterFieldName {
  Email = 'email',
  Password = 'password'
}

const RegisterFormContext = React.createContext<RegisterFormContextProps | null>(null)

/**
 * This function is used to disable the submit button
 * We want to disable submit for 2 cases:
 *   - There is an error
 *   - the fields are not touched AND we can't get the value for at least one of them (with shortlinks we might have fields that are not touched yet have a value)
 * @param form - Antd form instance
 * @returns Disable state of register button
 */
export const registerHasErrors = (form: FormInstance, registeredFields: Set<ComposedRegisterFieldName>) => {
  const { getFieldsError, isFieldsTouched, getFieldValue } = form

  return (
    (!isFieldsTouched(true) &&
      ((registeredFields.has(ComposedRegisterFieldName.Email) && !getFieldValue('email')) ||
        !getFieldValue('name') ||
        (registeredFields.has(ComposedRegisterFieldName.Password) && !getFieldValue('password')))) ||
    Boolean(getFieldsError().filter(({ errors }) => errors.length).length) ||
    getFieldValue('agreement') === false
  )
}

function ComposedRegisterForm({
  initialName,
  initialEmail,
  initialAgreement,
  registerButtonText,
  registerButtonDisabled,
  onSubmit,
  loading,
  children,
  form: overwriteForm,
  actions,
  ...rest
}: IRegisterFormProps) {
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })

  const [antdForm] = Form.useForm()
  const form = overwriteForm || antdForm
  // eslint-disable-next-line react/hook-use-state -- It's ok here
  const [, forceUpdate] = React.useState()

  // To disable submit button at the beginning.
  useEffect(() => {
    forceUpdate(undefined)
  }, [])

  const [registeredFields, setRegisteredFields] = React.useState(new Set<ComposedRegisterFieldName>())
  const registerField = React.useCallback(
    (name: ComposedRegisterFieldName) => {
      registeredFields.add(name)
      setRegisteredFields(new Set(registeredFields))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Keep it minimal
    []
  )
  const value = React.useMemo(() => ({ registerField }), [registerField])

  const handleSubmit = (values: RegisterFormPayload) => {
    onSubmit && onSubmit(values)
  }

  return (
    <RegisterFormContext.Provider value={value}>
      <Spin spinning={loading ?? false}>
        <Form
          layout="vertical"
          form={form}
          initialValues={{ email: initialEmail, name: initialName }}
          className={styles.form}
          onFinish={handleSubmit}
          requiredMark={false}
          {...rest}
        >
          <Form.Item
            name="name"
            label={<FormattedMessage id="components.registerScreen.form.label.name" />}
            rules={[{ required: true, message: <FormattedMessage id="required" /> }]}
          >
            <Input disabled={Boolean(initialName)} placeholder="John Doe" />
          </Form.Item>
          {children}
          <div className="u-padding-top--medium" />
          <CommutifiTermsCheckbox
            className={styles['agreement-container']}
            name="agreement"
            initialValue={initialAgreement}
            tabIndex={0}
          />
          {actions === null || actions ? (
            actions
          ) : (
            <Form.Item shouldUpdate>
              {(formInstance) => (
                <div className={styles['button-container']}>
                  <AppButton
                    type="primary"
                    htmlType="submit"
                    disabled={
                      !isNil(registerButtonDisabled)
                        ? registerButtonDisabled
                        : registerHasErrors(formInstance, registeredFields)
                    }
                    block={isXSmall}
                  >
                    {registerButtonText || <FormattedMessage id="components.registerScreen.form.button.submit" />}
                  </AppButton>
                </div>
              )}
            </Form.Item>
          )}
        </Form>
      </Spin>
    </RegisterFormContext.Provider>
  )
}

const useRegisterFormContext = () => {
  const context = React.useContext(RegisterFormContext)
  if (!context) {
    return {} as Partial<RegisterFormContextProps>
  }
  return context
}

interface PasswordFieldProps {
  /**
   * Overwrite default label used in PasswordInput common component
   */
  label?: React.ReactNode
  /**
   * Add extra item aligned on the far right of the label (space between)
   */
  extra?: React.ReactNode
  /**
   * Use Form Item help property to display hint under the password field.
   */
  hint?: React.ReactNode
  /**
   * Overwrite default rules
   */
  rules?: FormRule[]
  tabIndex?: number
}

function PasswordField({ extra, label, hint, rules, tabIndex }: PasswordFieldProps) {
  const { registerField } = useRegisterFormContext()
  React.useEffect(() => {
    registerField && registerField(ComposedRegisterFieldName.Password)
  }, [registerField])
  const intl = useIntl()

  return (
    <Form.Item noStyle shouldUpdate>
      {({ getFieldsError }) => {
        const passwordErrors = getFieldsError(['password'])?.[0]?.errors
        return (
          <PasswordInput
            rules={rules}
            colon={false}
            label={
              <div className="u-flex-space-between u-flex-align-center" style={{ width: '100%' }}>
                <span>{label || <FormattedMessage id="components.registerScreen.form.label.password" />}</span>
                {extra}
              </div>
            }
            labelCol={{ span: 24 }}
            help={passwordErrors.length ? passwordErrors : hint}
          >
            <Password
              tabIndex={tabIndex}
              allowClear
              placeholder={intl.formatMessage({ id: 'registration.inputs.password.placeholder' })}
            />
          </PasswordInput>
        )
      }}
    </Form.Item>
  )
}

function EmailField(props: React.ComponentProps<typeof Input>) {
  const { registerField } = useRegisterFormContext()
  React.useEffect(() => {
    registerField && registerField(ComposedRegisterFieldName.Email)
  }, [registerField])
  return (
    <Form.Item noStyle shouldUpdate>
      {({ isFieldTouched, getFieldValue }) => (
        <EmailInput
          colon={false}
          label={<FormattedMessage id="components.registerScreen.form.label.email" />}
          normalize={(value) => (value ? value.trim() : value)}
        >
          <Email disabled={!isFieldTouched('email') && getFieldValue('email')} autoComplete="email" {...props} />
        </EmailInput>
      )}
    </Form.Item>
  )
}

ComposedRegisterForm.PasswordField = PasswordField
ComposedRegisterForm.EmailField = EmailField

export function AppRegister(props: IRegisterFormProps) {
  return (
    <ComposedRegisterForm {...props}>
      <ComposedRegisterForm.EmailField />
      <ComposedRegisterForm.PasswordField hint={<FormattedMessage id={INTL_ID_VALIDATION_REQUIREMENTS} />} />
    </ComposedRegisterForm>
  )
}

export const RegisterForm = ComposedRegisterForm
